import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  CircularProgress,
  IconButton
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as BackIcon,
  Business as BusinessIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import ApiService from '../services/api.service';
import { isPlatformLevel, can } from '../utils/roleUtils';
import { resolveImageUrl } from '../utils/colorUtils';

// Compact 13px fields, like the rest of the app.
const FORM_SX = {
  '& .MuiInputBase-input, & .MuiSelect-select': { fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem' },
  '& .MuiFormHelperText-root': { fontSize: '0.6875rem' },
};
const SECTION_SX = { fontSize: '0.875rem', fontWeight: 600, color: 'secondary.main', mb: 1.5 };

const CompanyConfig = ({ currentUser, embedded = false }) => {
  const navigate = useNavigate();
  // Role check helper
  const isPlatform = isPlatformLevel(currentUser?.role);
  const canEdit = can(currentUser, 'settings', 'write');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Read-only brand logo, sourced from the typing center's theme_config (the single
  // source of truth). It is set on the Tenant Settings page, not here.
  const [themeLogoUrl, setThemeLogoUrl] = useState('');
  const [logoBroken, setLogoBroken] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [typingCenters, setTypingCenters] = useState([]);
  const [selectedTypingCenter, setSelectedTypingCenter] = useState('');

  // Company config state
  const [config, setConfig] = useState({
    companyName: '',
    companyNameArabic: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    emirate: '',
    country: 'UAE',
    postalCode: '',
    telephone: '',
    mobile: '',
    email: '',
    website: '',
    trnNumber: '',
    tradeLicenseNumber: ''
  });

  const emirates = [
    'Abu Dhabi',
    'Ajman',
    'Dubai',
    'Fujairah',
    'Ras Al Khaimah',
    'Sharjah',
    'Umm Al Quwain'
  ];

  // Fetch typing centers
  useEffect(() => {
    const fetchTypingCenters = async () => {
      try {
        const response = await ApiService.getTypingCenters();
        if (response.data?.data) {
          setTypingCenters(response.data.data);
          if (response.data.data.length > 0) {
            if (currentUser?.typingCenterId) {
              setSelectedTypingCenter(currentUser.typingCenterId);
            } else {
              setSelectedTypingCenter(response.data.data[0].id);
            }
          }
        }
      } catch (err) {
        console.error('Error fetching typing centers:', err);
        setError('Failed to load typing centers');
      }
    };
    fetchTypingCenters();
  }, [currentUser]);

  // Fetch config when typing center changes
  useEffect(() => {
    if (selectedTypingCenter) {
      fetchConfig();
      fetchThemeLogo();
    }
  }, [selectedTypingCenter]);

  // Read the brand logo from the selected TC's theme_config (single source of truth).
  const fetchThemeLogo = async () => {
    try {
      const res = await ApiService.getTypingCenterById(selectedTypingCenter);
      const tc = res.data?.data || res.data || {};
      let logo = '';
      const raw = tc.theme_config ?? tc.themeConfig;
      if (raw) {
        const theme = typeof raw === 'string' ? JSON.parse(raw) : raw;
        logo = theme.logo_url || theme.logoUrl || '';
      }
      setThemeLogoUrl(logo);
    } catch (err) {
      console.error('Error fetching theme logo:', err);
      setThemeLogoUrl('');
    }
  };

  const fetchConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await ApiService.getCompanyConfigById(selectedTypingCenter);
      if (response.data?.data) {
        const data = response.data.data;
        const tc = typingCenters.find(tc => tc.id === selectedTypingCenter);
        // Managers serializes JSON as snake_case, so read snake_case keys
        // (camelCase fallback kept in case the response shape ever changes).
        setConfig({
          companyName: data.company_name || data.companyName || tc?.name || '',
          companyNameArabic: data.company_name_arabic || data.companyNameArabic || '',
          addressLine1: data.address_line1 || data.addressLine1 || '',
          addressLine2: data.address_line2 || data.addressLine2 || '',
          city: data.city || '',
          emirate: data.emirate || '',
          country: data.country || 'UAE',
          postalCode: data.postal_code || data.postalCode || '',
          telephone: data.telephone || '',
          mobile: data.mobile || '',
          email: data.email || '',
          website: data.website || '',
          trnNumber: data.trn_number || data.trnNumber || '',
          tradeLicenseNumber: data.trade_license_number || data.tradeLicenseNumber || ''
        });
      }
    } catch (err) {
      console.error('Error fetching company config:', err);
      if (err.response?.status === 404) {
        // No config yet — pre-fill company name from typing center
        const tc = typingCenters.find(tc => tc.id === selectedTypingCenter);
        if (tc) {
          setConfig(prev => ({
            ...prev,
            companyName: tc.name || tc.companyName || '',
            email: tc.email || '',
            telephone: tc.phone || tc.telephone || '',
          }));
        }
      } else {
        setError('Failed to load company configuration');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfigChange = (field) => (event) => {
    setConfig(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await ApiService.updateCompanyConfigById(selectedTypingCenter, config);
      setSuccess('Company configuration saved successfully');
    } catch (err) {
      console.error('Error saving company config:', err);
      setError(err.response?.data?.message || 'Failed to save company configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !selectedTypingCenter) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header — hidden when shown inside Organizations › Our Company */}
      {!embedded && (
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
          <BackIcon />
        </IconButton>
        <BusinessIcon sx={{ mr: 1, color: 'secondary.main' }} />
        <Typography variant="h5" fontWeight={600}>
          Company Configuration
        </Typography>
      </Box>
      )}

      {!canEdit && <Alert severity="info" sx={{ mb: 2 }}>You have view-only access to these settings.</Alert>}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Typing Center Selector */}
      {isPlatform && typingCenters.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Select Typing Center</InputLabel>
            <Select
              value={selectedTypingCenter}
              label="Select Typing Center"
              onChange={(e) => setSelectedTypingCenter(e.target.value)}
            >
              {typingCenters.map(tc => (
                <MenuItem key={tc.id} value={tc.id}>
                  {tc.name} ({tc.code})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ p: 2.5, ...FORM_SX }}>
          <Typography sx={SECTION_SX}>Company Information</Typography>
          <Grid container spacing={1.5} alignItems="center">
            {/* Logo — set in Branding; shown here so the company card is complete. */}
            <Grid item xs={12} sm="auto">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{
                  width: 56, height: 56, borderRadius: 1.5, border: '1px solid', borderColor: 'divider', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.paper', overflow: 'hidden',
                }}>
                  {/* A stored URL that no longer loads falls back to the placeholder instead of alt text. */}
                  {themeLogoUrl && !logoBroken
                    ? <img src={resolveImageUrl(themeLogoUrl)} alt="" onError={() => setLogoBroken(true)} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    : <ImageIcon sx={{ fontSize: 28, color: 'text.disabled' }} />}
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                    {!themeLogoUrl ? 'No logo set' : logoBroken ? 'Logo could not be loaded' : 'Logo'}
                  </Typography>
                  <Button size="small" onClick={() => navigate('/settings?section=company&tab=branding')}
                    sx={{ textTransform: 'none', fontSize: '0.75rem', p: 0, minWidth: 0 }}>
                    Change in Branding
                  </Button>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} sm>
              <TextField label="Company Name (English)" value={config.companyName} onChange={handleConfigChange('companyName')} size="small" disabled={!canEdit} fullWidth required />
            </Grid>
            <Grid item xs={12} sm>
              <TextField label="Company Name (Arabic)" value={config.companyNameArabic} onChange={handleConfigChange('companyNameArabic')} size="small" disabled={!canEdit} fullWidth dir="rtl" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Address Line 1" value={config.addressLine1} onChange={handleConfigChange('addressLine1')} size="small" disabled={!canEdit} fullWidth />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Address Line 2" value={config.addressLine2} onChange={handleConfigChange('addressLine2')} size="small" disabled={!canEdit} fullWidth />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField label="City" value={config.city} onChange={handleConfigChange('city')} size="small" disabled={!canEdit} fullWidth />
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Emirate</InputLabel>
                <Select value={config.emirate} label="Emirate" onChange={handleConfigChange('emirate')} disabled={!canEdit}>
                  {emirates.map(e => (
                    <MenuItem key={e} value={e} sx={{ fontSize: '0.8125rem' }}>{e}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField label="Country" value={config.country || ''} onChange={handleConfigChange('country')} size="small" disabled={!canEdit} fullWidth placeholder="UAE" />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField label="Postal Code" value={config.postalCode} onChange={handleConfigChange('postalCode')} size="small" disabled={!canEdit} fullWidth />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography sx={SECTION_SX}>Contact Information</Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField label="Telephone" value={config.telephone} onChange={handleConfigChange('telephone')} size="small" disabled={!canEdit} fullWidth placeholder="+971 4 XXX XXXX" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField label="Mobile" value={config.mobile} onChange={handleConfigChange('mobile')} size="small" disabled={!canEdit} fullWidth placeholder="+971 5X XXX XXXX" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField label="Email" value={config.email} onChange={handleConfigChange('email')} size="small" disabled={!canEdit} fullWidth type="email" />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField label="Website" value={config.website} onChange={handleConfigChange('website')} size="small" disabled={!canEdit} fullWidth placeholder="https://www.example.com" />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography sx={SECTION_SX}>Tax & Legal Information</Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <TextField label="TRN (Tax Registration Number)" value={config.trnNumber} onChange={handleConfigChange('trnNumber')} size="small" disabled={!canEdit} fullWidth
                placeholder="100XXXXXXXXX" helperText="15-digit UAE Tax Registration Number" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Trade License Number" value={config.tradeLicenseNumber} onChange={handleConfigChange('tradeLicenseNumber')} size="small" disabled={!canEdit} fullWidth />
            </Grid>
          </Grid>

          {canEdit && (
            <Box mt={2} display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                size="small"
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon sx={{ fontSize: 16 }} />}
                onClick={handleSave}
                disabled={saving}
                sx={{ textTransform: 'none', fontSize: '0.8125rem' }}
              >
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default CompanyConfig;
